import { useRef, useState, useEffect } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import parse from "html-react-parser";

import Button from "src/components/atomic/Button";
import UiConfigButton from "../../atomic/UiConfigButton/UiConfigButton";
import PopoverPanel from "../../atomic/Dropdown/PopoverPanel";
import type {
  IBaseUiConfigButtonConfig,
  IUiConfigButtonClientSideInput
} from "../../atomic/UiConfigButton/types";

import { checkVisibility } from "../../../services/ApiResponseMiddleware";
import { useBuildPanel } from "../BuilderPanel/useBuildPanel";

interface UIConfigListProps {
  sectionData: any;
  items: any[];
  loanApplicationID?: string;
  userID?: string;
  token?: string;
  pageName?: string;
  isLenderDashboard?: boolean;
  tabName?: string;
  onDataRefresh?: () => void;
  activeTab?: string;
  whiteLabelStyle?: any;
}

function ListActionMenuItem({
  btnKey,
  btn,
  clientSideInput,
  loanApplicationID,
  tabName,
  isLenderDashboard,
  onClickFinished
}: {
  btnKey: string;
  btn: any;
  clientSideInput: IUiConfigButtonClientSideInput;
  loanApplicationID: string;
  tabName: string;
  isLenderDashboard: boolean;
  onClickFinished: (
    response: any,
    buttonConfig: IBaseUiConfigButtonConfig
  ) => void;
}) {
  const hiddenBtnRef = useRef<HTMLDivElement>(null);
  const trigger = () => hiddenBtnRef.current?.querySelector("button")?.click();

  return (
    <Button
      type="button"
      className="text-sm text-left w-full text-gray-700 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-2 cursor-pointer rounded transition-colors whitespace-nowrap"
      onClick={trigger}
    >
      {btn.label}
      <div ref={hiddenBtnRef} className="sr-only">
        <UiConfigButton
          buttonConfig={{ key: btnKey, ...btn }}
          clientSideInput={clientSideInput}
          loanApplicationID={loanApplicationID}
          tabName={tabName}
          isLenderDashboard={isLenderDashboard}
          onClickFinished={onClickFinished}
        />
      </div>
    </Button>
  );
}

function CellWithShowMore({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [text]);

  return (
    <div className="w-full max-w-full grid grid-cols-[1fr_auto] items-start gap-1">
      {/* TEXT */}
      <div
        ref={ref}
        className={`text-xs text-gray-500 ${expanded ? "whitespace-pre-line break-words [&_img]:max-w-full [&_img]:h-auto [&_img]:mt-2 [&_img]:block [&_img]:rounded" : "whitespace-nowrap overflow-hidden text-ellipsis [&_*]:inline [&_br]:hidden [&_img]:hidden"}`}
      >
        {parse(text)}
      </div>

      {/* SHOW MORE / HIDE */}
      {isOverflowing && !expanded && (
        <button
          className="text-blue-600 text-xs"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      )}

      {expanded && (
        <button
          className="text-blue-600 text-xs col-span-2"
          onClick={() => setExpanded(false)}
        >
          Hide
        </button>
      )}
    </div>
  );
}

export default function UIConfigList({
  sectionData,
  items,
  loanApplicationID,
  userID,
  token,
  pageName,
  isLenderDashboard,
  tabName,
  onDataRefresh,
  activeTab,
  whiteLabelStyle
}: UIConfigListProps) {
  const columnFields: any[] = (items?.[0]?.nestedfieldList ?? []).filter(
    checkVisibility
  );

  const hasAnyActions = items?.some((item) =>
    Object.values(item?.buttonList ?? {}).some((btn: any) =>
      checkVisibility(btn)
    )
  );

  const { handleClickFinished, builderPanelNode } = useBuildPanel({
    token,
    pageName,
    activeTab,
    whiteLabelStyle,
    isLenderDashboard,
    staticContext: {
      loanApplicationData: {
        loanApplicationID,
        userID,
        sectionID: sectionData?.id,
        resourceName: tabName
      }
    },
    onDataRefresh
  });

  /**
   * Build a per-row clientSideInput by flattening the row's nestedfieldList
   * into { [field.id]: field.value } so that template expressions like
   * "${clientSideInput.collateral_id}" are resolved by UiConfigButton.
   */
  const buildClientSideInput = (item: any): IUiConfigButtonClientSideInput => {
    const nestedValues = (item?.nestedfieldList ?? []).reduce(
      (acc: Record<string, string>, field: any) => ({
        ...acc,
        [field.id]: String(field.value ?? "")
      }),
      {}
    );

    return {
      loanApplicationID: loanApplicationID || "",
      userID: userID || "",
      token: token || "",
      taskID: item.value,
      ...nestedValues
    };
  };

  return (
    <>
      <div className="sm:ring-1 ring-gray-200 relative rounded-lg max-w-full overflow-hidden mt-4">
        <div className="max-w-full max-h-[55vh] overflow-auto rounded-lg">
          <table
            className="table-fixed w-full relative"
            style={{ borderSpacing: 0 }}
          >
            <thead>
              <tr>
                {columnFields.map((field: any, i: number) => (
                  <th
                    key={field.id ? `${field.id}-${i}` : `hdr-${i}`}
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 whitespace-nowrap bg-white px-3 py-4 text-center text-xs font-semibold text-gray-900 truncate z-10"
                  >
                    {field.label}
                  </th>
                ))}

                {hasAnyActions && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 whitespace-nowrap bg-white px-3 py-4 text-left text-xs font-semibold text-gray-900 truncate z-10"
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="bg-white overflow-visible">
              {items?.map((item: any, rowIdx: number) => {
                const visibleFields: any[] = (
                  item?.nestedfieldList ?? []
                ).filter(checkVisibility);

                const visibleButtons: [string, any][] = item?.buttonList
                  ? (Object.entries(item.buttonList) as [string, any][]).filter(
                      ([_, btn]) => checkVisibility(btn)
                    )
                  : [];

                const clientSideInput = buildClientSideInput(item);

                return (
                  <tr
                    key={item?.value || item?.id || `row-${rowIdx}`}
                    className="overflow-visible hover:bg-indigo-50"
                  >
                    {visibleFields.map((field: any, colIdx: number) => (
                      <td
                        key={
                          field.id
                            ? `${field.id}-${rowIdx}-${colIdx}`
                            : `cell-${rowIdx}-${colIdx}`
                        }
                        className="border-b text-center border-gray-200 px-3 py-4 text-xs text-gray-500"
                      >
                        <CellWithShowMore text={String(field.value ?? "-")} />
                      </td>
                    ))}

                    {hasAnyActions && (
                      <td className="border-b border-gray-200 px-3 py-4 text-xs text-gray-500 overflow-visible whitespace-nowrap">
                        {visibleButtons.length > 0 && (
                          <div className="flex justify-start">
                            <PopoverPanel
                              showArrow={false}
                              icon={
                                <EllipsisVerticalIcon className="h-4 w-4 !text-inherit hover:text-inherit" />
                              }
                              styles={{
                                childContainer:
                                  "overflow-visible shadow-none !ring-0",
                                button:
                                  "!px-1 !py-1 border !text-[#8D8D8E] border-[#8D8D8E] hover:border-indigo-700 hover:!text-indigo-700 bg-white"
                              }}
                            >
                              <div className="flex flex-col items-stretch w-full py-1">
                                {visibleButtons.map(
                                  ([btnKey, btn]: [string, any]) => (
                                    <ListActionMenuItem
                                      key={btnKey}
                                      btnKey={btnKey}
                                      btn={btn}
                                      clientSideInput={clientSideInput}
                                      loanApplicationID={
                                        loanApplicationID || ""
                                      }
                                      tabName={tabName || ""}
                                      isLenderDashboard={
                                        isLenderDashboard || false
                                      }
                                      onClickFinished={(
                                        response: any,
                                        buttonConfig: IBaseUiConfigButtonConfig
                                      ) =>
                                        handleClickFinished(
                                          response,
                                          buttonConfig
                                        )
                                      }
                                    />
                                  )
                                )}
                              </div>
                            </PopoverPanel>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {builderPanelNode}
    </>
  );
}
